import React from 'react';
import Icon from './Icon';
import Eyebrow from './page/Eyebrow';
import { tie } from './page/typography';

/**
 * Вступ сторінки: лід-абзац і те, що йде за ним.
 *
 * Історія цього місця. Спершу кожна сторінка складала вступ сама, і на
 * «Послугах» це виросло в чотири блоки один під одним — читалося як
 * передмова до передмови. Потім з'явився `aside`: текст ліворуч, перелік
 * праворуч, і ті самі слова стали займати один екран замість трьох.
 *
 * Сторінки без переліку лишалися з однією вузькою колонкою, а вся права
 * половина екрана — порожня. Власник це й побачив: «багато пустого місця
 * між текстом». Тому вступ розкладається на дві колонки — лід ліворуч,
 * решта абзаців праворуч. Якщо абзац один, ділити нема чого: він іде на всю
 * ширину більшим кеглем і працює як епіграф сторінки.
 *
 * Перелік (`aside`) тепер за замовчуванням іде під текстом смугою на всю
 * ширину. Збоку, у картці на 20rem, він не вміщався: пункти в п'ять рядків
 * по три слова, номери 01–05, які нічого не означали, і порожнеча під
 * лівою колонкою, бо картка виходила вищою за текст. У смузі пункти стоять
 * в один ряд плиток, текст у них не рветься, а висота блоку визначається
 * найдовшим пунктом, а не їх кількістю.
 *
 * Опції:
 * - `layout="side"` — стара розкладка «текст ліворуч, перелік праворуч»,
 *   для сторінок, де текст помітно довший за перелік;
 * - `asideFirst` — смуга над текстом, коли перелік і є головним (на
 *   телефоні він тоді теж перший);
 * - без `paragraphs`, лише з `aside` — сама смуга.
 */
const leadClass =
  'text-lg lg:text-xl leading-relaxed text-slate-800 border-l-2 border-teal-400 pl-5 lg:pl-6';
const restClass = 'text-base lg:text-lg leading-relaxed text-slate-600';

const PageIntro = ({ paragraphs = [], aside, layout = 'band', asideFirst = false, className = '' }) => {
  const [lead, ...rest] = paragraphs;
  if (!lead && !aside) return null;

  if (lead && aside && layout === 'side') {
    return (
      <div className={`grid gap-10 items-start lg:grid-cols-[minmax(0,1fr)_minmax(0,24rem)] lg:gap-14 ${className}`}>
        <div className="max-w-2xl">
          <p className={leadClass}>{tie(lead)}</p>
          {rest.map((paragraph) => (
            <p key={paragraph.slice(0, 40)} className={`${restClass} mt-5 pl-5 lg:pl-6`}>
              {tie(paragraph)}
            </p>
          ))}
        </div>
        <AsideCard aside={aside} />
      </div>
    );
  }

  const text = lead ? <IntroText lead={lead} rest={rest} /> : null;
  if (!aside) return <div className={className}>{text}</div>;

  const band = <AsideBand aside={aside} />;
  return (
    <div className={`space-y-10 lg:space-y-12 ${className}`}>
      {asideFirst ? band : text}
      {asideFirst ? text : band}
    </div>
  );
};

const IntroText = ({ lead, rest }) => {
  // Один абзац — на всю ширину.
  if (!rest.length) {
    return <p className={`${leadClass} max-w-5xl`}>{tie(lead)}</p>;
  }

  // Два і більше — лід ліворуч, решта праворуч.
  return (
    <div className="grid gap-8 lg:grid-cols-2 lg:gap-14 items-start">
      <p className={leadClass}>{tie(lead)}</p>
      <div className="space-y-5">
        {rest.map((paragraph) => (
          <p key={paragraph.slice(0, 40)} className={restClass}>
            {tie(paragraph)}
          </p>
        ))}
      </div>
    </div>
  );
};

/*
 * Скільки плиток у ряд і коли іконка стає над текстом. Класи виписані
 * повністю, бо Tailwind шукає їх у тексті файлу й зібраний із шматків рядок
 * не побачить.
 *
 * Іконка піднімається над текстом лише там, де весь перелік уміщується в
 * один ряд: від xl до п'яти пунктів (навіть по сто знаків — чотири рядки
 * тексту), шість коротких. Нижче — рядки «іконка ліворуч, текст праворуч» у
 * дві колонки: плитки 3+2 на ноутбуці 1024 лишали порожню клітинку на пів
 * смуги, а п'ять вузьких колонок — текст по три слова в рядку.
 */
const tileLayout = (n, longest) => {
  const xlStack = { item: 'xl:flex-col xl:gap-4', text: 'xl:pt-0' };
  if (n <= 1) return { grid: '', item: '', text: '' };
  if (n === 2) return { grid: 'sm:grid-cols-2', item: '', text: '' };
  if (n === 3) return { grid: 'md:grid-cols-3', item: 'md:flex-col md:gap-4', text: 'md:pt-0' };
  if (n <= 5) return { grid: `sm:grid-cols-2 ${n === 4 ? 'xl:grid-cols-4' : 'xl:grid-cols-5'}`, ...xlStack };
  if (n === 6 && longest <= 40) return { grid: 'sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6', ...xlStack };
  if (n <= 8 && longest <= 60) return { grid: 'sm:grid-cols-2 xl:grid-cols-4', ...xlStack };
  return { grid: 'sm:grid-cols-2 xl:grid-cols-3', item: '', text: '' };
};

// Посилання коротші за пункти-пояснення, тож їх уміщується більше в ряд.
const linkColumns = (n) => {
  if (n <= 1) return '';
  if (n === 2 || n === 4) return 'sm:grid-cols-2 lg:grid-cols-4';
  if (n % 3 === 0) return 'sm:grid-cols-2 lg:grid-cols-3';
  return 'sm:grid-cols-2 lg:grid-cols-4';
};

/**
 * Перелік смугою на всю ширину.
 *
 * Якщо всі пункти — посилання, це зміст сторінки: він стає <nav>, а пункти
 * виглядають як кнопки зі стрілкою, щоб було видно, що на них натискають.
 * Інакше — плитки з іконкою (див. tileLayout).
 *
 * Номерів 01–05 більше немає: порядок у цих переліках нічого не означає, а
 * цифри поруч з іконкою лише додавали ще один стовпчик, через який текст
 * пункту ставав вужчим.
 */
const AsideBand = ({ aside }) => {
  const items = aside.items || [];
  const allLinks = items.length > 0 && items.every((item) => item.href);
  const Wrapper = allLinks ? 'nav' : 'div';
  const longest = items.reduce((max, item) => Math.max(max, item.label.length), 0);
  const tiles = tileLayout(items.length, longest);

  return (
    <Wrapper
      aria-label={allLinks ? aside.title : undefined}
      className="rounded-2xl border border-slate-100 bg-slate-50 p-5 sm:p-8 lg:p-10"
    >
      {aside.title && (
        <Eyebrow as="h2" className="mb-6">
          {aside.title}
        </Eyebrow>
      )}

      {allLinks ? (
        <ul className={`list-none grid gap-3 ${linkColumns(items.length)}`}>
          {items.map((item) => (
            <li key={item.label}>
              <LinkChip item={item} />
            </li>
          ))}
        </ul>
      ) : (
        <ul className={`list-none grid gap-x-8 gap-y-5 lg:gap-y-6 ${tiles.grid}`}>
          {items.map((item) => (
            <li key={item.label} className={`flex items-start gap-4 ${tiles.item}`}>
              {item.icon && (
                <span
                  className="flex-shrink-0 w-10 h-10 rounded-xl bg-teal-50 text-teal-700 flex items-center justify-center"
                  aria-hidden="true"
                >
                  <Icon name={item.icon} />
                </span>
              )}
              <span className={`${item.icon ? 'pt-2' : ''} ${tiles.text} text-[0.95rem] leading-relaxed font-medium text-slate-800`}>
                {item.href ? (
                  <a
                    href={item.href}
                    className="underline decoration-teal-300 underline-offset-4 hover:text-teal-800 hover:decoration-teal-600 transition-colors duration-200"
                  >
                    {tie(item.label)}
                  </a>
                ) : (
                  tie(item.label)
                )}
              </span>
            </li>
          ))}
        </ul>
      )}

      {aside.note && (
        <div className="mt-8 pt-6 border-t border-slate-200">
          <p className="max-w-4xl text-base leading-relaxed text-slate-600">{tie(aside.note)}</p>
        </div>
      )}
    </Wrapper>
  );
};

/**
 * Пункт змісту. Стрілка вниз — для якоря на цій самій сторінці, вправо —
 * для переходу на іншу: людина наперед бачить, що станеться після дотику.
 * min-h-[3.25rem] — ціль для пальця з запасом, навіть коли підпис в один
 * рядок.
 */
const LinkChip = ({ item }) => {
  const local = item.href.startsWith('#');
  return (
    <a
      href={item.href}
      className="group flex h-full min-h-[3.25rem] items-center gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3 hover:border-teal-300 hover:shadow-sm transition-[border-color,box-shadow] duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-700 focus-visible:ring-offset-2"
    >
      {item.icon && <Icon name={item.icon} className="flex-shrink-0 text-sm text-teal-700" />}
      <span className="flex-1 min-w-0 text-[0.95rem] font-medium leading-snug text-slate-800 group-hover:text-teal-800 transition-colors duration-200">
        {tie(item.label)}
      </span>
      <Icon
        name="fa-arrow-right"
        className={`flex-shrink-0 text-xs text-teal-700 ${local ? 'rotate-90' : ''}`}
      />
    </a>
  );
};

/**
 * Перелік карткою збоку — для layout="side". Той самий зміст, що в смузі,
 * тільки в одну колонку і без номерів.
 */
const AsideCard = ({ aside }) => (
  <div className="rounded-2xl border border-slate-200 bg-gradient-to-br from-slate-50 to-white p-6 lg:p-7">
    {aside.title && (
      <Eyebrow as="h2" className="mb-5">
        {aside.title}
      </Eyebrow>
    )}
    <ul className="list-none space-y-3">
      {aside.items.map((item) =>
        item.href ? (
          <li key={item.label}>
            <LinkChip item={item} />
          </li>
        ) : (
          <li key={item.label} className="flex items-start gap-3">
            {item.icon && (
              <span
                className="flex-shrink-0 w-9 h-9 rounded-lg bg-teal-50 text-teal-700 flex items-center justify-center"
                aria-hidden="true"
              >
                <Icon name={item.icon} />
              </span>
            )}
            <span className="pt-1.5 text-[0.95rem] leading-snug text-slate-700">{tie(item.label)}</span>
          </li>
        )
      )}
    </ul>
    {aside.note && (
      <p className="mt-5 pt-5 border-t border-slate-200 text-sm leading-relaxed text-slate-600">
        {tie(aside.note)}
      </p>
    )}
  </div>
);

export default PageIntro;
